// src/pages/Home.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertCircle, Loader } from 'lucide-react';
import NetworkAdvisor from "../components/NetworkAdvisor";
import "./Home.css";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================
const API_CONFIG = {
  BASE_URL: 'https://cms.vxpath.com/wp-json/wp/v2', // ✅ FIXED: removed trailing spaces
  ENDPOINTS: {
    SLIDES: '/slide?per_page=10&_embed',
    SERVICE_CATEGORIES: '/service_category?per_page=20',
    TESTIMONIALS: '/testimonial?per_page=20'
  },
  TIMEOUT: 10000,
};

const CAROUSEL_CONFIG = {
  SLIDE_INTERVAL: 8000,
  TRANSITION_DURATION: 600,
  SERVICES_PER_PAGE: 3,
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const sanitizeHTML = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
};

const createSlug = (text) => {
  if (!text) return '';
  return text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
};

// Map WordPress category slugs to your Services tab IDs
const mapCategorySlugToTabId = (wpSlug) => {
  const mapping = {
    'network': 'network',
    'security': 'net-security',
    'net-security': 'net-security',
    'automation': 'ai-automation',
    'ai-automation': 'ai-automation',
    'ai': 'ai-automation',        // ✅ map "ai" → "ai-automation"
    'wireless': 'wireless',
    'surveillance': 'surveillance',
    'technician': 'net-technician',
    'net-technician': 'net-technician',
    'cloud-network': 'cloud-network',
    'cloud': 'cloud-network',     // ✅ optional fallback
  };
  return mapping[wpSlug] || 'network'; // fallback to network
};

const fetchWithTimeout = async (url, timeout = API_CONFIG.TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw error;
  }
};

// ============================================================================
// ICON & DESCRIPTION MAPPING
// ============================================================================

const getCategoryIcon = (slug) => {
  const icons = {
    'network': '🌐',
    'net-security': '🛡️',
    'security': '🛡️',
    'ai-automation': '🧬',
    'automation': '🧬',
    'Ai-Automation': '🧬',
    'ai':'🧬',
    'Ai':'🧬',
    'wireless': '📡',
    'Cloud-Network': '☁️',
    'cloud-network': '☁️',
    'surveillance': '📹',
    'net-technician': '🔧',
    'technician': '🔧',
    
  };
  return icons[slug] || '⚙️';
};

const getCategoryDescription = (name) => {
  const descriptions = {
    'Network': 'Enterprise networking solutions including routing, switching, and infrastructure design',
    'Net-Security': 'Network security services including firewalls, IDS/IPS, and threat protection',
    'Ai&Automation': 'Network automation and orchestration using modern DevOps tools and practices',
    'Wireless': 'Wireless networking solutions including WiFi, mobility, and location services',
    'Surveillance': 'IP camera systems, video surveillance, and security monitoring solutions',
    'Net-Tech Support': 'On-site technical support, installation, and maintenance services',
    'Cloud-Network': 'Hybrid cloud connectivity, multi-cloud networking, and cloud-native infrastructure',
  };
  return descriptions[name] || `Explore our ${name} solutions`;
};

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

const transformSlideData = (wpSlide) => {
  const id = (wpSlide.acf?.slide_id || wpSlide.slug || `slide-${wpSlide.id}`).trim();
  
  let imageUrl = '';
  if (wpSlide.acf?.hero_image && typeof wpSlide.acf.hero_image === 'string') {
    imageUrl = wpSlide.acf.hero_image.trim();
  } else if (wpSlide.featured_media_src_url) {
    imageUrl = wpSlide.featured_media_src_url.trim();
  } else if (wpSlide._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
    imageUrl = wpSlide._embedded['wp:featuredmedia'][0].source_url.trim();
  }

  let ctaLink = '/services';
  const rawCategory = wpSlide.acf?.related_category_slug?.trim();
  if (rawCategory) {
    const safeSlug = createSlug(rawCategory);
    const tabId = mapCategorySlugToTabId(safeSlug);
    ctaLink = `/services#${tabId}`;
  }

  return {
    id,
    imageUrl,
    title: sanitizeHTML(wpSlide.title?.rendered) || 'Untitled Slide',
    subtitle: (wpSlide.acf?.subtitle || '').trim(),
    description: (wpSlide.acf?.description || '').trim(),
    ctaLink,
  };
};

const transformTestimonialData = (wpTestimonial) => ({
  quote: (wpTestimonial.acf?.quote || '').trim(),
  author: (wpTestimonial.acf?.author || '').trim(),
});

// ============================================================================
// CUSTOM HOOK
// ============================================================================

const useWordPressContent = () => {
  const [state, setState] = useState({
    slides: [],
    categories: [],
    testimonials: [],
    loading: { slides: true, categories: true, testimonials: true },
    errors: { slides: null, categories: null, testimonials: null },
  });

  useEffect(() => {
    const fetchAllContent = async () => {
      // Fetch slides
      try {
        const slidesData = await fetchWithTimeout(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SLIDES}`
        );
        const formattedSlides = slidesData
          .map(transformSlideData)
          .filter((slide) => slide.imageUrl);
        
        setState((prev) => ({
          ...prev,
          slides: formattedSlides,
          loading: { ...prev.loading, slides: false },
        }));
      } catch (error) {
        console.error('Error fetching slides:', error);
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, slides: false },
          errors: { ...prev.errors, slides: error.message },
        }));
      }

      // Fetch service CATEGORIES
      try {
        const categoriesData = await fetchWithTimeout(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SERVICE_CATEGORIES}`
        );

        const formattedCategories = categoriesData.map((cat) => {
          const tabId = mapCategorySlugToTabId(cat.slug);
          return {
            id: cat.id,
            title: cat.name || 'Untitled',
            desc: getCategoryDescription(cat.name),
            icon: getCategoryIcon(cat.slug),
            link: `/services#${tabId}`, // ✅ Links to correct tab
            slug: tabId,
          };
        });

        // Hardcode "Network Q&A" as first item
        const allCards = [
          {
            id: 'qa',
            title: 'Network Q&A',
            desc: 'Get instant answers to your networking questions',
            icon: '❓',
            action: 'advisor',
            isNetworkQA: true,
          },
          ...formattedCategories
        ];

        setState((prev) => ({
          ...prev,
          categories: allCards,
          loading: { ...prev.loading, categories: false },
        }));
      } catch (error) {
        console.error('Error fetching categories:', error);
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, categories: false },
          errors: { ...prev.errors, categories: error.message },
        }));
      }

      // Fetch testimonials
      try {
        const testimonialsData = await fetchWithTimeout(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TESTIMONIALS}`
        );
        const formattedTestimonials = testimonialsData
          .map(transformTestimonialData)
          .filter((t) => t.quote);
        
        setState((prev) => ({
          ...prev,
          testimonials: formattedTestimonials,
          loading: { ...prev.loading, testimonials: false },
        }));
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, testimonials: false },
          errors: { ...prev.errors, testimonials: error.message },
        }));
      }
    };

    fetchAllContent();
  }, []);

  return state;
};

const useCarouselAutoRotation = (itemCount, currentIndex, onNext, interval = CAROUSEL_CONFIG.SLIDE_INTERVAL) => {
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (itemCount <= 1 || isPaused) return;

    intervalRef.current = setInterval(() => {
      onNext();
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [itemCount, isPaused, onNext, interval, currentIndex]);

  return { isPaused, setIsPaused };
};

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

const ServiceCard = React.memo(({ title, desc, icon, link, onClick, isNetworkQA }) => {
  if (onClick) {
    // Q&A card: button behavior
    return (
      <div
        className={`service-card-carousel ${isNetworkQA ? 'service-card-qa' : ''}`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={`Open ${title}`}
      >
        <div className="service-icon-large" aria-hidden="true">
          {icon}
        </div>
        <h3 className="service-card-title">{title}</h3>
        <p className="service-card-desc">{desc}</p>
      </div>
    );
  }

  // Regular category card: Link
  return (
    <Link
      to={link}
      className={`service-card-carousel ${isNetworkQA ? 'service-card-qa' : ''}`}
      aria-label={`View ${title} services`}
    >
      <div className="service-icon-large" aria-hidden="true">
        {icon}
      </div>
      <h3 className="service-card-title">{title}</h3>
      <p className="service-card-desc">{desc}</p>
      <span className="service-card-link">Learn More →</span>
    </Link>
  );
});

ServiceCard.displayName = 'ServiceCard';

const ErrorMessage = ({ message, onRetry }) => (
  <div className="error-message" role="alert">
    <AlertCircle size={20} />
    <span>{message}</span>
    {onRetry && (
      <button onClick={onRetry} className="retry-button">
        Retry
      </button>
    )}
  </div>
);

const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="loading-container" role="status">
    <Loader className="spinner" size={32} />
    <span className="sr-only">{message}</span>
    <p className="loading-text">{message}</p>
  </div>
);

const HeroSlide = React.memo(({ slide, isActive, isTransitioning }) => (
  <div
    className={`slide-enhanced ${isActive ? "active" : ""} ${isTransitioning ? "transitioning" : ""}`}
    aria-hidden={!isActive}
  >
    <div className="slide-image-overlay-gradient" />
    <img
      src={slide.imageUrl}
      alt={slide.title}
      className="slide-image-enhanced"
      loading={isActive ? "eager" : "lazy"}
    />
    <div className="slide-content-enhanced">
      {slide.description && (
        <div className="slide-badge">{slide.description}</div>
      )}
      <h1>{slide.title}</h1>
      {slide.subtitle && <p>{slide.subtitle}</p>}
      <div className="hero-cta-enhanced">
        <Link to={slide.ctaLink} className="btn-primary-glow">
          Explore Services
        </Link>
        <Link to="/contact" className="btn-secondary-glass">
          Contact Experts
        </Link>
      </div>
    </div>
  </div>
));

HeroSlide.displayName = 'HeroSlide';

// ============================================================================
// MAIN HOME COMPONENT
// ============================================================================
const Home = () => {
  const { slides, categories, testimonials, loading, errors } = useWordPressContent();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Slide navigation
  const handleNextSlide = useCallback(() => {
    if (slides.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
    }, CAROUSEL_CONFIG.TRANSITION_DURATION);
  }, [slides.length, isTransitioning]);

  const handlePrevSlide = useCallback(() => {
    if (slides.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setIsTransitioning(false);
    }, CAROUSEL_CONFIG.TRANSITION_DURATION);
  }, [slides.length, isTransitioning]);

  const handleSlideSelect = useCallback((index) => {
    if (index === currentSlide || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, CAROUSEL_CONFIG.TRANSITION_DURATION);
  }, [currentSlide, isTransitioning]);

  const { setIsPaused: setSlidesPaused } = useCarouselAutoRotation(
    slides.length,
    currentSlide,
    handleNextSlide
  );

  // Services carousel logic
  const handleNextService = useCallback(() => {
    const maxIndex = categories.length - CAROUSEL_CONFIG.SERVICES_PER_PAGE;
    if (currentServiceIndex < maxIndex) {
      setCurrentServiceIndex(Math.min(currentServiceIndex + CAROUSEL_CONFIG.SERVICES_PER_PAGE, maxIndex));
    }
  }, [currentServiceIndex, categories.length]);

  const handlePrevService = useCallback(() => {
    if (currentServiceIndex > 0) {
      setCurrentServiceIndex(Math.max(0, currentServiceIndex - CAROUSEL_CONFIG.SERVICES_PER_PAGE));
    }
  }, [currentServiceIndex]);

  const handleServicePageSelect = useCallback((pageIndex) => {
    setCurrentServiceIndex(pageIndex * CAROUSEL_CONFIG.SERVICES_PER_PAGE);
  }, []);

  const visibleServices = useMemo(() => 
    categories.slice(currentServiceIndex, currentServiceIndex + CAROUSEL_CONFIG.SERVICES_PER_PAGE),
    [categories, currentServiceIndex]
  );

  const totalServicePages = Math.ceil(categories.length / CAROUSEL_CONFIG.SERVICES_PER_PAGE);
  const currentServicePage = Math.floor(currentServiceIndex / CAROUSEL_CONFIG.SERVICES_PER_PAGE);

  // Advisor modal
  const openAdvisor = useCallback(() => setShowAdvisor(true), []);
  const closeAdvisor = useCallback(() => setShowAdvisor(false), []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showAdvisor) {
        closeAdvisor();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showAdvisor, closeAdvisor]);

  // Loading state
  if (loading.slides && slides.length === 0) {
    return (
      <div className="home-modern">
        <section className="hero-section-enhanced">
          <div className="hero-slideshow-enhanced">
            <LoadingSpinner message="Loading hero content..." />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="home-modern">
      {/* HERO SECTION */}
      <section 
        className="hero-section-enhanced"
        onMouseEnter={() => setSlidesPaused(true)}
        onMouseLeave={() => setSlidesPaused(false)}
        aria-label="Hero slideshow"
      >
        <div className="hero-animated-bg" aria-hidden="true" />
        <div className="hero-slideshow-enhanced">
          <button
            className="slide-arrow prev"
            onClick={handlePrevSlide}
            disabled={isTransitioning || slides.length <= 1}
            aria-label="Previous slide"
          >
            <ChevronLeft size={28} />
          </button>

          {slides.length > 0 ? (
            slides.map((slide, index) => (
              <HeroSlide
                key={slide.id}
                slide={slide}
                isActive={index === currentSlide}
                isTransitioning={isTransitioning}
              />
            ))
          ) : errors.slides ? (
            <div className="slide-enhanced active">
              <div className="slide-content-enhanced">
                <ErrorMessage 
                  message={`Failed to load slides: ${errors.slides}`}
                  onRetry={() => window.location.reload()}
                />
              </div>
            </div>
          ) : (
            <div className="slide-enhanced active">
              <div className="slide-content-enhanced">
                <h1>No Slides Available</h1>
                <p>Please add slides in WordPress admin</p>
                <Link to="/contact" className="btn-primary-glow">
                  Contact Support
                </Link>
              </div>
            </div>
          )}

          <button
            className="slide-arrow next"
            onClick={handleNextSlide}
            disabled={isTransitioning || slides.length <= 1}
            aria-label="Next slide"
          >
            <ChevronRight size={28} />
          </button>

          {slides.length > 1 && (
            <div className="slide-indicators-enhanced" role="tablist" aria-label="Slide navigation">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  role="tab"
                  className={`indicator-enhanced ${index === currentSlide ? "active" : ""}`}
                  onClick={() => handleSlideSelect(index)}
                  aria-label={`Go to slide ${index + 1}: ${slide.title}`}
                  aria-selected={index === currentSlide}
                  aria-controls={`slide-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SERVICES CAROUSEL SECTION */}
      <section className="services-carousel-section" aria-label="Our services">
        <div className="container">
          <div className="section-header-enhanced">
            <h2>Our Premium Solutions</h2>
            <p>Enterprise-grade services designed for tomorrow's challenges</p>
          </div>

          {loading.categories ? (
            <LoadingSpinner message="Loading solutions..." />
          ) : errors.categories ? (
            <ErrorMessage 
              message={`Failed to load services: ${errors.categories}`}
              onRetry={() => window.location.reload()}
            />
          ) : categories.length > 0 ? (
            <>
              <div className="carousel-container">
                <button
                  className="carousel-nav-btn prev"
                  onClick={handlePrevService}
                  disabled={currentServiceIndex === 0}
                  aria-label="Previous services"
                >
                  <ChevronLeft size={24} />
                </button>

                <div className="services-carousel-grid" role="list">
                  {visibleServices.map((category) => (
                    <ServiceCard
                      key={category.id}
                      title={category.title}
                      desc={category.desc}
                      icon={category.icon}
                      link={category.link}
                      onClick={category.action === "advisor" ? openAdvisor : null}
                      isNetworkQA={category.isNetworkQA}
                    />
                  ))}
                </div>

                <button
                  className="carousel-nav-btn next"
                  onClick={handleNextService}
                  disabled={currentServiceIndex + CAROUSEL_CONFIG.SERVICES_PER_PAGE >= categories.length}
                  aria-label="Next services"
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              {totalServicePages > 1 && (
                <div className="carousel-progress">
                  <div className="progress-dots" role="tablist" aria-label="Service pages">
                    {Array.from({ length: totalServicePages }).map((_, i) => (
                      <button
                        key={i}
                        role="tab"
                        className={`progress-dot ${currentServicePage === i ? "active" : ""}`}
                        onClick={() => handleServicePageSelect(i)}
                        aria-label={`Go to page ${i + 1}`}
                        aria-selected={currentServicePage === i}
                      />
                    ))}
                  </div>
                  <div className="progress-text" aria-live="polite">
                    Showing {currentServiceIndex + 1}–
                    {Math.min(currentServiceIndex + CAROUSEL_CONFIG.SERVICES_PER_PAGE, categories.length)} of {categories.length}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="empty-state">No services available at this time.</p>
          )}

          {/* TESTIMONIALS */}
          {testimonials.length > 0 && (
            <div className="auto-scroll-section" aria-label="Client testimonials">
              <h3>What Our Clients Say</h3>
              <div className="scroll-container">
                <div className="scroll-content" aria-live="off">
                  {[...testimonials, ...testimonials].map((item, i) => (
                    <div className="scroll-item" key={`testimonial-${i}`}>
                      <span className="quote">"{item.quote}"</span>
                      <span className="author">— {item.author}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ADVISOR MODAL */}
      {showAdvisor && (
        <div
          className="advisor-modal-overlay-enhanced"
          onClick={closeAdvisor}
          role="dialog"
          aria-modal="true"
          aria-labelledby="advisor-modal-title"
        >
          <div
            className="advisor-modal-enhanced"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-enhanced"
              onClick={closeAdvisor}
              aria-label="Close advisor modal"
            >
              ✕
            </button>
            <NetworkAdvisor onClose={closeAdvisor} />
          </div>
        </div>
      )}

      {/* CONTACT CTA */}
      <section className="contact-cta-enhanced" aria-label="Contact us">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Infrastructure?</h2>
            <p>Schedule a free consultation with our solutions architects</p>
            <Link to="/contact" className="btn-primary-large">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;